import { useMemo } from "react";

import * as Sentry from "@sentry/react";

import type { RecordingError, RecordingErrorKind } from "./use-media-recorder";
import type { StreamError, StreamErrorKind } from "./use-media-stream";
import type { ResolvedVideoProfile, VideoProfile } from "./video-profile";

const CATEGORY = "webcam-capture";

/** Which product surface a capture event came from. */
export type CaptureSurface = "socratic" | "device_check" | "work_attachment";

/** Surface for a caller that didn't wire up `useWebcamCaptureTelemetry`. */
export type ReportedSurface = CaptureSurface | "unknown";

/**
 * Sentry reporting for the shared webcam-capture stack.
 */
export function webcamBreadcrumb(
  message: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({ category: CATEGORY, level: "info", message, data });
}

export function captureWebcamWarning(
  message: string,
  tags: Record<string, string | number | boolean>,
  extra: Record<string, unknown>
): void {
  Sentry.captureMessage(message, { level: "warning", tags, extra });
}

/**
 * Records what the Media Capabilities probe decided. Breadcrumb only: this
 * fires once per capture session for every student.
 */
export function reportVideoProfileResolved({
  profile,
  outcome,
  contentType,
}: ResolvedVideoProfile): void {
  webcamBreadcrumb("video profile resolved", {
    video_profile: profile.kind,
    video_probe_outcome: outcome,
    video_bitrate: profile.bitrate,
    probe_content_type: contentType ?? "none",
  });
}

export interface RecordingStartedInfo {
  recordingKind: "audio" | "video";
  /** The profile actually applied, which is always `standard` outside video mode. */
  profile: VideoProfile;
  /** Bitrate handed to MediaRecorder, after any cap or caller override. */
  bitrate: number;
  /** Codec the recording's first attempt started on. */
  mimeType: string;
}

/**
 * Records the settings a recording actually started with.
 */
export function reportRecordingStarted(
  surface: ReportedSurface,
  { recordingKind, profile, bitrate, mimeType }: RecordingStartedInfo
): void {
  const tags = {
    capture_surface: surface,
    recording_kind: recordingKind,
    video_profile: profile.kind,
    mime_type: mimeType,
  };

  webcamBreadcrumb("recording started", { ...tags, bitrate });

  if (recordingKind === "video" && profile.kind === "capped") {
    captureWebcamWarning(
      "Web capture recording at capped video profile",
      tags,
      { bitrate }
    );
  }
}

/** Context shared by every audio-graph event, so they filter together. */
export interface AudioGraphTelemetry {
  captureSurface: ReportedSurface;
  /** Socratic dialogue the capture belongs to, on surfaces that have one. */
  dialogueId?: string;
}

function audioGraphTags(
  { captureSurface, dialogueId }: AudioGraphTelemetry,
  contextState: string
): Record<string, string> {
  return {
    capture_surface: captureSurface,
    dialogue_id: dialogueId ?? "none",
    context_state: contextState,
  };
}

/**
 * The audio graph never reached `running` at record start, so the recorder was
 * pointed at the raw stream instead of the graph's destination track.
 */
export function reportAudioGraphNotRunning(
  telemetry: AudioGraphTelemetry,
  contextState: string,
  waitedMs: number
): void {
  const tags = audioGraphTags(telemetry, contextState);
  webcamBreadcrumb("audio graph not running at record start", tags);
  captureWebcamWarning(
    "audio graph not running at record start; recorded raw stream",
    tags,
    { graph_ready_timeout_ms: waitedMs }
  );
}

/** Outcome of the `resume()` call at record start. */
export type ResumeOutcome = "resolved" | "rejected";

/**
 * The context moved state, or `resume()` settled. Together these name the
 * audio-graph bug as the cause of a silent recording: a context that went
 * suspended mid-recording, or a `resume()` that never settled at all — which
 * shows up as neither of these breadcrumbs appearing.
 */
export function reportAudioContextStateChange(
  telemetry: AudioGraphTelemetry,
  contextState: string
): void {
  webcamBreadcrumb(
    "audio context statechange",
    audioGraphTags(telemetry, contextState)
  );
}

export function reportAudioContextResumeOutcome(
  telemetry: AudioGraphTelemetry,
  outcome: ResumeOutcome,
  contextState: string
): void {
  webcamBreadcrumb("audio context resume settled", {
    ...audioGraphTags(telemetry, contextState),
    resume_outcome: outcome,
  });
}

/**
 * The level taps stopped because the browser closed the context under us. A
 * closed context keeps handing out zeros, so without this breadcrumb a dead
 * meter cannot be told from a microphone that has gone quiet.
 */
export function reportAudioGraphContextClosed(
  telemetry: AudioGraphTelemetry
): void {
  webcamBreadcrumb(
    "audio level taps stopped: context closed",
    audioGraphTags(telemetry, "closed")
  );
}

/** A device was added or removed while a capture surface was open. */
export function reportDeviceChange(
  surface: ReportedSurface,
  diff: { added: string[]; removed: string[] },
  counts: { audioInputs: number; videoInputs: number }
): void {
  webcamBreadcrumb("devicechange", {
    capture_surface: surface,
    added: diff.added,
    removed: diff.removed,
    audio_inputs: counts.audioInputs,
    video_inputs: counts.videoInputs,
  });
}

/** A rejected `AudioContext` call — carries the same context as the warning. */
export function captureAudioGraphException(
  error: unknown,
  telemetry: AudioGraphTelemetry,
  contextState: string
): void {
  Sentry.captureException(error, {
    level: "warning",
    tags: audioGraphTags(telemetry, contextState),
  });
}

const HARDWARE_STREAM_KINDS: ReadonlySet<StreamErrorKind> = new Set([
  "not_found",
  "overconstrained",
  "unsupported",
  "unknown",
]);

const NON_ALERTABLE_RECORDING_ERROR_KINDS: ReadonlySet<RecordingErrorKind> =
  new Set(["size_exceeded", "duration_exceeded"]);

export interface UseWebcamCaptureTelemetryOptions {
  surface: CaptureSurface;
  /** Socratic dialogue the capture belongs to, on surfaces that have one. */
  dialogueId?: string;
}

/**
 * Handlers for telemetry events from the webcam capture stack
 */
export function useWebcamCaptureTelemetry({
  surface,
  dialogueId,
}: UseWebcamCaptureTelemetryOptions): {
  captureSurface: CaptureSurface;
  dialogueId: string | undefined;
  onStreamError: (error: StreamError) => void;
  onRecordingError: (error: RecordingError) => void;
} {
  return useMemo(
    () => ({
      captureSurface: surface,
      dialogueId,
      onStreamError(error) {
        webcamBreadcrumb("getUserMedia failed", {
          capture_surface: surface,
          stream_error_kind: error.kind,
        });
        if (HARDWARE_STREAM_KINDS.has(error.kind)) {
          captureWebcamWarning(
            "Web capture could not access device",
            { capture_surface: surface, stream_error_kind: error.kind },
            { message: error.message }
          );
        }
      },
      onRecordingError(error) {
        const tags = {
          capture_surface: surface,
          recording_error_kind: error.kind,
          recording_error_name: error.domErrorName ?? "none",
        };
        webcamBreadcrumb("recording failed", tags);
        if (!NON_ALERTABLE_RECORDING_ERROR_KINDS.has(error.kind)) {
          captureWebcamWarning("Web capture recording failed", tags, {
            message: error.message,
          });
        }
      },
    }),
    [surface, dialogueId]
  );
}
